import { useEffect, useState } from 'react';

export function usePersistentReviewUiState(sessionId: string, defaultSectionKeys: readonly string[]) {
  const storageKey = `enmon-inventory-review-ui:${sessionId}`;
  const [activeSectionKeys, setActiveSectionKeysState] = useState<string[]>(() => {
    const saved = readReviewUiState(storageKey);

    return saved.activeSectionKeys.length > 0 ? saved.activeSectionKeys : [...defaultSectionKeys];
  });

  useEffect(() => {
    const saved = readReviewUiState(storageKey);
    setActiveSectionKeysState(saved.activeSectionKeys.length > 0 ? saved.activeSectionKeys : [...defaultSectionKeys]);
  }, [defaultSectionKeys, storageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const saved = readReviewUiState(storageKey);
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    if (saved.scrollY <= 0 && !saved.focusedFieldId) {
      return () => {
        window.history.scrollRestoration = previousScrollRestoration;
      };
    }

    let secondFrame = 0;
    const timeout = window.setTimeout(() => restoreReviewPosition(saved), 150);
    const firstFrame = window.requestAnimationFrame(() => {
      restoreReviewPosition(saved);
      secondFrame = window.requestAnimationFrame(() => restoreReviewPosition(saved));
    });

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      window.clearTimeout(timeout);
    };
  }, [storageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let frame = 0;
    const rememberScrollPosition = () => {
      if (frame) {
        return;
      }

      frame = window.requestAnimationFrame(() => {
        frame = 0;
        writeReviewUiState(storageKey, { scrollY: readCurrentScrollY() });
      });
    };

    window.addEventListener('scroll', rememberScrollPosition, { passive: true });
    window.addEventListener('pagehide', rememberScrollPosition);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }

      writeReviewUiState(storageKey, { scrollY: readCurrentScrollY() });
      window.removeEventListener('scroll', rememberScrollPosition);
      window.removeEventListener('pagehide', rememberScrollPosition);
    };
  }, [storageKey]);

  function setActiveSectionKeys(keys: string | string[]) {
    const nextKeys = (Array.isArray(keys) ? keys : [keys]).filter(Boolean);
    setActiveSectionKeysState(nextKeys);
    writeReviewUiState(storageKey, { activeSectionKeys: nextKeys });
  }

  function rememberScrollPosition(focusedFieldId?: string) {
    writeReviewUiState(storageKey, { focusedFieldId, scrollY: readCurrentScrollY() });
  }

  return {
    activeSectionKeys,
    rememberScrollPosition,
    setActiveSectionKeys,
  };
}

interface ReviewUiState {
  activeSectionKeys: string[];
  focusedFieldId?: string;
  scrollY: number;
}

function readReviewUiState(storageKey: string): ReviewUiState {
  if (typeof globalThis.sessionStorage === 'undefined') {
    return createDefaultReviewUiState();
  }

  try {
    const parsed: unknown = JSON.parse(globalThis.sessionStorage.getItem(storageKey) ?? '{}');

    if (!parsed || typeof parsed !== 'object') {
      return createDefaultReviewUiState();
    }

    const state = parsed as Partial<ReviewUiState>;

    return {
      activeSectionKeys: Array.isArray(state.activeSectionKeys)
        ? state.activeSectionKeys.filter((value): value is string => typeof value === 'string')
        : [],
      focusedFieldId: typeof state.focusedFieldId === 'string' ? state.focusedFieldId : undefined,
      scrollY: typeof state.scrollY === 'number' && Number.isFinite(state.scrollY) ? state.scrollY : 0,
    };
  } catch {
    return createDefaultReviewUiState();
  }
}

function writeReviewUiState(storageKey: string, patch: Partial<ReviewUiState>) {
  if (typeof globalThis.sessionStorage === 'undefined') {
    return;
  }

  const current = readReviewUiState(storageKey);
  globalThis.sessionStorage.setItem(storageKey, JSON.stringify({ ...current, ...patch }));
}

function createDefaultReviewUiState(): ReviewUiState {
  return {
    activeSectionKeys: [],
    focusedFieldId: undefined,
    scrollY: 0,
  };
}

function readCurrentScrollY() {
  if (typeof window === 'undefined') {
    return 0;
  }

  return Math.max(window.scrollY, document.documentElement.scrollTop, document.body.scrollTop);
}

function restoreReviewPosition(saved: ReviewUiState) {
  if (saved.focusedFieldId && scrollFieldIntoView(saved.focusedFieldId)) {
    return;
  }

  window.scrollTo({ top: saved.scrollY });
}

function scrollFieldIntoView(fieldId: string) {
  const field = Array.from(document.querySelectorAll<HTMLElement>('[data-review-field-id]')).find(
    element => element.dataset.reviewFieldId === fieldId,
  );

  if (!field) {
    return false;
  }

  field.scrollIntoView({ block: 'center' });

  return true;
}
